import { isInLineOfSight } from "../boardUtil";
import { ALL_DIRECTIONS, directionDelta } from "../direction";
import type { DistanceMap } from "../distanceMap";
import type { ControllableByAI } from "../interfaces";
import { breadthFirstSearch } from "../levelgenerator/levelGeneratorUtils";
import { MarkovChain } from "../markovChain";
import { MobCorpse } from "../mobs/mobCorpse";
import { isPassableThroughDoors } from "../passability";
import { randomChance, randomElement, randomInt } from "../random";
import { deserializeXY, getMobFactory, serializeXY } from "../serialization";
import type { Sound } from "../things/sounds/sound";
import { WoodenDoor } from "../things/woodenDoor";
import { XY } from "../xy";
import { findPath, type StepCost } from "./pathfinder";
import {
	bumpDoorIfClosed,
	canMobStandIn,
	findSidestepTowards,
	isPositionInconvenient,
	updateLookDirectionFromHorizontalDelta,
} from "./movement";

export const REPAIR_TURNS = 6;
export const SCAN_PERIOD = 10;
export const FLEE_TRIGGER_RANGE = 6;
export const FLEE_SAFE_DISTANCE = 14;
/** Flee-cost falloff: cell cost = 1 + max(0, BUBBLE - playerDist), distance-map units. */
const FLEE_PLAYER_BUBBLE = 5;
const RANDOM_WANDER_MOVE_PROBABILITY = 0.3;
const STALL_GIVE_UP_TURNS = 5;

export enum EngineerLogicState {
	WANDERING = "WANDERING",
	MOVING_TO_CORPSE = "MOVING_TO_CORPSE",
	REPAIRING = "REPAIRING",
	FLEEING = "FLEEING",
}

enum WanderingType {
	STOPPED = "STOPPED",
	RANDOM_WANDER = "RANDOM_WANDER",
}

const wanderMarkov = new MarkovChain<WanderingType>();
wanderMarkov.addConfig(WanderingType.STOPPED, WanderingType.STOPPED, 75);
wanderMarkov.addConfig(WanderingType.STOPPED, WanderingType.RANDOM_WANDER, 25);
wanderMarkov.addConfig(WanderingType.RANDOM_WANDER, WanderingType.RANDOM_WANDER, 85);
wanderMarkov.addConfig(WanderingType.RANDOM_WANDER, WanderingType.STOPPED, 15);

export interface EngineerActor extends ControllableByAI {
	getLogicState(): EngineerLogicState;
	setLogicState(state: EngineerLogicState): void;
	getRepairTurnsLeft(): number;
	setRepairTurnsLeft(n: number): void;
}

export class EngineerAI {
	private actor: EngineerActor;
	private wanderingType: WanderingType = WanderingType.STOPPED;
	// Random phase staggers the response and de-syncs multiple engineers.
	private scanCooldown: number = randomInt(1, SCAN_PERIOD);
	private distressTarget: XY | null = null;
	private stallTurns = 0;
	private bestPathLength = Infinity;

	fleeSoundSupplier: (() => Sound) | null = null;
	repairSoundSupplier: (() => Sound) | null = null;
	wanderSoundSupplier: (() => Sound) | null = null;

	constructor(actor: EngineerActor) {
		this.actor = actor;
	}

	processTurn(): void {
		if (this.shouldFlee()) this.enterFlee();

		const state = this.actor.getLogicState();
		if (state === EngineerLogicState.FLEEING) this.processFlee();
		else if (state === EngineerLogicState.REPAIRING) this.processRepair();
		else this.processIdleSearch();
	}

	private processIdleSearch(): void {
		if (this.tryEngageCorpse()) {
			this.distressTarget = null;
			return;
		}
		if (this.respondToDistress()) return;
		this.enterWandering();
		this.wander();
	}

	// Commits to a location, not a corpse: travels even if the corpse is destroyed
	// en route. Gives up only when the path is gone or stops getting shorter.
	private respondToDistress(): boolean {
		if (this.distressTarget === null) this.scanForDistress();
		const target = this.distressTarget;
		if (target === null) return false;

		if (this.actor.getXY().distanceTo(target) <= 1) {
			this.distressTarget = null;
			return false;
		}
		const path = findPath(this.actor.getBoard(), this.actor.getXY(), target, this.doorPassableStepCost());
		if (!path || path.length === 0 || this.isStalled(path.length)) {
			this.distressTarget = null;
			return false;
		}
		this.tryMove(path[0], target);
		this.actor.setLogicState(EngineerLogicState.MOVING_TO_CORPSE);
		return true;
	}

	// Remaining path length is the progress metric: position can churn forever
	// against a blocker (sidestep dances), but the path never gets shorter.
	private isStalled(pathLength: number): boolean {
		if (pathLength < this.bestPathLength) {
			this.bestPathLength = pathLength;
			this.stallTurns = 0;
			return false;
		}
		this.stallTurns++;
		return this.stallTurns >= STALL_GIVE_UP_TURNS;
	}

	private scanForDistress(): void {
		this.scanCooldown--;
		if (this.scanCooldown > 0) return;
		this.scanCooldown = SCAN_PERIOD;
		this.distressTarget = this.findDistressLocation();
		this.stallTurns = 0;
		this.bestPathLength = Infinity;
	}

	private findDistressLocation(): XY | null {
		const reachable = breadthFirstSearch(this.actor.getXY(), (xy) => this.isTraversableWithDoors(xy));
		return this.nearestRepairableCorpse(reachable, false)?.xy ?? null;
	}

	private tryEngageCorpse(): boolean {
		const corpse = this.findRoomCorpse();
		if (!corpse) return false;
		const approach = this.pickCorpseApproachCell(corpse);
		if (!approach) return false;

		if (this.actor.getXY().equals(approach)) {
			this.enterRepair(corpse);
		} else {
			this.actor.setLogicState(EngineerLogicState.MOVING_TO_CORPSE);
			this.moveTowardsTarget(approach);
		}
		return true;
	}

	/** Cardinal neighbor of the corpse to repair from. Diagonal stops read as "too far away". */
	private pickCorpseApproachCell(corpse: MobCorpse): XY | null {
		const board = this.actor.getBoard();
		const here = this.actor.getXY();

		const cardinals = corpse.xy.getCardinalNeighbors().filter((n) => board.isInBounds(n));
		for (const n of cardinals) {
			if (n.equals(here)) return here;
		}

		const candidates = cardinals
			.filter((n) => board.isPassable(n) && !board.hasMob(n))
			.sort((a, b) => a.distanceTo(here) - b.distanceTo(here));
		return candidates[0] ?? null;
	}

	// --- Wander ---

	private wander(): void {
		this.wanderingType = wanderMarkov.run(this.wanderingType);

		// Never park on a door (holds it open) — force a step off, settle next turn.
		const inconvenient = isPositionInconvenient(this.actor.getBoard(), this.actor.getXY());
		if (inconvenient) this.wanderingType = WanderingType.RANDOM_WANDER;
		if (this.wanderingType === WanderingType.STOPPED) return;

		if (!inconvenient && !randomChance(RANDOM_WANDER_MOVE_PROBABILITY)) return;
		this.makeRandomStep();
		if (inconvenient) this.wanderingType = WanderingType.STOPPED;
	}

	private makeRandomStep(): void {
		const board = this.actor.getBoard();
		const here = this.actor.getXY();
		const passable: XY[] = [];
		for (const dir of ALL_DIRECTIONS) {
			const proposed = here.add(directionDelta(dir));
			if (canMobStandIn(board, proposed)) passable.push(proposed);
		}
		const dest = randomElement(passable);
		if (dest) this.tryMove(dest);
	}

	// --- Flee ---

	private shouldFlee(): boolean {
		const board = this.actor.getBoard();
		const player = board.player;
		if (!player || !player.active) return false;
		const here = this.actor.getXY();
		if (here.distanceTo(player.xy) > FLEE_TRIGGER_RANGE) return false;
		return isInLineOfSight(board, here, player.xy);
	}

	private enterFlee(): void {
		if (this.actor.getLogicState() !== EngineerLogicState.FLEEING) {
			this.addSound(this.fleeSoundSupplier);
		}
		this.actor.setLogicState(EngineerLogicState.FLEEING);
		this.actor.setRepairTurnsLeft(0);
	}

	private processFlee(): void {
		const board = this.actor.getBoard();
		const player = board.player;
		const distMap = board.getDistanceMapToPlayerIncludingDoors();
		if (!player || !player.active || !distMap) {
			this.enterWandering();
			return;
		}
		const here = this.actor.getXY();
		const myDist = distMap.get(here);
		if (myDist !== undefined && myDist >= FLEE_SAFE_DISTANCE) {
			this.enterWandering();
			return;
		}

		const target = this.findFleeTarget(distMap);
		if (!target) return;

		const path = findPath(board, here, target, this.fleeStepCost(distMap));
		if (!path || path.length === 0) return;
		this.tryMove(path[0], target);
	}

	/** Like doorPassableStepCost but with a falloff penalty near the player so A* arcs around. */
	private fleeStepCost(distMap: DistanceMap): StepCost {
		return (_from, to) => {
			if (!this.isTraversableWithDoors(to)) return null;
			const playerDist = distMap.get(to);
			if (playerDist === undefined) return 1;
			return 1 + Math.max(0, FLEE_PLAYER_BUBBLE - playerDist);
		};
	}

	private findFleeTarget(distMap: DistanceMap): XY | null {
		const here = this.actor.getXY();
		const reachable = breadthFirstSearch(here, (xy) => this.isTraversableWithDoors(xy));
		const myDist = distMap.get(here) ?? 0;

		let best: XY | null = null;
		let bestDist = myDist;
		for (const xy of reachable) {
			const d = distMap.get(xy);
			if (d === undefined) continue;
			if (d > bestDist) {
				bestDist = d;
				best = xy;
			}
		}
		return best;
	}

	// --- Repair ---

	private enterRepair(corpse: MobCorpse): void {
		// An unoccupied opened door never auto-closes, so one bump covers the whole repair.
		bumpDoorIfClosed(this.actor.getBoard(), corpse.xy, this.actor as any);
		this.addSound(this.repairSoundSupplier);
		this.actor.setLogicState(EngineerLogicState.REPAIRING);
		this.actor.setRepairTurnsLeft(REPAIR_TURNS);
		updateLookDirectionFromHorizontalDelta(this.actor, this.actor.getXY(), corpse.xy);
	}

	private enterWandering(): void {
		if (this.actor.getLogicState() !== EngineerLogicState.WANDERING) {
			this.addSound(this.wanderSoundSupplier);
		}
		this.actor.setLogicState(EngineerLogicState.WANDERING);
	}

	private processRepair(): void {
		const board = this.actor.getBoard();
		const corpse = this.findAdjacentRepairableCorpse();
		if (!corpse) {
			this.enterWandering();
			this.actor.setRepairTurnsLeft(0);
			return;
		}

		const left = this.actor.getRepairTurnsLeft() - 1;
		this.actor.setRepairTurnsLeft(left);
		if (left > 0) return;

		const mobKey = corpse.getRepairableMobKey()!;
		const xy = corpse.xy;
		// A mob crossing mid-repair can re-close a door here; don't spawn inside it.
		bumpDoorIfClosed(board, xy, this.actor as any);
		board.removeThing(corpse);
		const newMob = getMobFactory(mobKey)();
		newMob.xy = xy;
		board.addThing(newMob);
		this.enterWandering();
	}

	private findAdjacentRepairableCorpse(): MobCorpse | null {
		const board = this.actor.getBoard();
		const here = this.actor.getXY();
		for (const n of here.getCardinalNeighbors()) {
			if (!board.isInBounds(n)) continue;
			for (const t of board.getThingsAt(n)) {
				if (t instanceof MobCorpse && t.getRepairableMobKey() !== null) return t;
			}
		}
		return null;
	}

	// --- Corpse search ---

	private findRoomCorpse(): MobCorpse | null {
		return this.nearestRepairableCorpse(this.roomCellsIncludingFrontierDoors(), true);
	}

	private nearestRepairableCorpse(cells: XY[], requireApproach: boolean): MobCorpse | null {
		const board = this.actor.getBoard();
		const here = this.actor.getXY();

		let closest: MobCorpse | null = null;
		let bestDist = Infinity;
		for (const xy of cells) {
			if (xy.equals(here)) continue; // can't repair from on top of the corpse
			const d = here.distanceTo(xy);
			if (d >= bestDist) continue;
			for (const t of board.getThingsAt(xy)) {
				if (!(t instanceof MobCorpse)) continue;
				if (t.getRepairableMobKey() === null) continue;
				// Skip unapproachable corpses so we don't fixate and ignore alternatives.
				if (requireApproach && this.pickCorpseApproachCell(t) === null) continue;
				bestDist = d;
				closest = t;
				break;
			}
		}
		return closest;
	}

	private roomCellsIncludingFrontierDoors(): XY[] {
		const board = this.actor.getBoard();
		const reachable = breadthFirstSearch(this.actor.getXY(), this.roomBfsPredicate());
		const seen = new Set(reachable.map((xy) => xy.toString()));
		const cells = [...reachable];
		for (const xy of reachable) {
			for (const n of xy.getNeighbors()) {
				if (!board.isInBounds(n) || seen.has(n.toString())) continue;
				if (board.getThingOfClass(WoodenDoor, n) === null) continue;
				seen.add(n.toString());
				cells.push(n);
			}
		}
		return cells;
	}

	// Doors bound rooms regardless of open/closed state — a Mob on a door holds it open
	// indefinitely (WoodenDoor.processTurn won't close while occupied), which would let
	// the engineer "hear" corpses several rooms away. A* still walks through doors via
	// doorPassableStepCost; only the room-detection BFS treats them as walls.
	private roomBfsPredicate(): (xy: XY) => boolean {
		const board = this.actor.getBoard();
		return (xy) => board.isInBounds(xy) && board.isPassable(xy) && board.getThingOfClass(WoodenDoor, xy) === null;
	}

	// --- Movement helpers ---

	private moveTowardsTarget(target: XY): boolean {
		const board = this.actor.getBoard();
		const here = this.actor.getXY();
		const path = findPath(board, here, target, this.doorPassableStepCost());
		if (!path || path.length === 0) return false;
		this.tryMove(path[0], target);
		return true;
	}

	private isTraversableWithDoors(xy: XY): boolean {
		return isPassableThroughDoors(this.actor.getBoard(), xy);
	}

	private doorPassableStepCost(): StepCost {
		return (_from, to) => (this.isTraversableWithDoors(to) ? 1 : null);
	}

	private tryMove(xy: XY, target: XY | null = null): void {
		const board = this.actor.getBoard();
		if (!board.isInBounds(xy)) return;

		// findPath is mob-unaware so paths can route through mobs. Sidestep instead
		// of stalling. Random wander passes target=null and just gives up.
		if (board.hasMob(xy) && target !== null) {
			const sidestep = findSidestepTowards(this.actor.getXY(), xy, target, (n) => canMobStandIn(board, n));
			if (sidestep === null) return;
			xy = sidestep;
		}

		bumpDoorIfClosed(board, xy, this.actor as any);
		if (!board.isPassable(xy)) return;
		if (board.hasMob(xy)) return;

		const before = this.actor.getXY();
		this.actor.moveTo(xy);
		updateLookDirectionFromHorizontalDelta(this.actor, before, xy);
	}

	private addSound(supplier: (() => Sound) | null): void {
		if (supplier === null) return;
		this.actor.getBoard().addNonPhysicalThing(supplier());
	}

	// Accumulated wander/scan intent that must survive save/resume.
	serialize(): Record<string, unknown> {
		return {
			wanderingType: this.wanderingType,
			scanCooldown: this.scanCooldown,
			distressTarget: this.distressTarget ? serializeXY(this.distressTarget) : null,
			stallTurns: this.stallTurns,
			// Infinity is not representable in JSON.
			bestPathLength: Number.isFinite(this.bestPathLength) ? this.bestPathLength : null,
		};
	}

	restoreFrom(data: any): void {
		if (!data) return;
		// Old saves may carry the removed MOVING_TO_DOOR wander state.
		this.wanderingType =
			data.wanderingType === WanderingType.RANDOM_WANDER ? WanderingType.RANDOM_WANDER : WanderingType.STOPPED;
		if (typeof data.scanCooldown === "number") this.scanCooldown = data.scanCooldown;
		this.distressTarget = data.distressTarget ? deserializeXY(data.distressTarget) : null;
		if (typeof data.stallTurns === "number") this.stallTurns = data.stallTurns;
		this.bestPathLength = typeof data.bestPathLength === "number" ? data.bestPathLength : Infinity;
	}
}
